import { Component, computed, inject, input, Signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { map, Observable } from 'rxjs';
import { ToastMessageOptions } from 'primeng/api';
import { AlertsService } from '../../services/alerts.service';
import { AlertToPrimengMessageConverter } from '../../converters/alert-to-primeng-message.converter';
import { Message } from 'primeng/message';
import { isEmptyString, isNotNil, Nillable } from '@web/utils';

@Component({
  selector: 'app-alerts',
  imports: [CommonModule, Message],
  templateUrl: './alerts.component.html',
  styleUrls: ['./alerts.component.scss'],
})
export class AlertsComponent {
  private static readonly allowedSeverities = new Set<
    NonNullable<Message['severity']>
  >(['success', 'info', 'warn', 'error', 'secondary', 'contrast']);

  private readonly alert = inject(AlertsService);
  private readonly converter = inject(AlertToPrimengMessageConverter);

  public readonly showIcon = input(false);
  public readonly messages$ = this.messagesSource();

  protected readonly styleClass = this.styleClassSignal();

  public shouldShowSummary(summary: Nillable<string>): summary is string {
    return isNotNil(summary) && !isEmptyString(summary);
  }

  public shouldShowDetail(detail: Nillable<string>): detail is string {
    return isNotNil(detail) && !isEmptyString(detail);
  }

  public toMessageSeverity(
    severity: ToastMessageOptions['severity'],
  ): Message['severity'] {
    if (
      isNotNil(severity) &&
      AlertsComponent.allowedSeverities.has(
        severity as NonNullable<Message['severity']>,
      )
    ) {
      return severity as NonNullable<Message['severity']>;
    }

    return null;
  }

  private messagesSource(): Observable<ToastMessageOptions[]> {
    return this.alert.show$.pipe(
      map((alerts) => alerts.map((alert) => this.converter.convert(alert))),
    );
  }

  private styleClassSignal(): Signal<string> {
    return computed(() => {
      const styleClassList: string[] = [];

      if (this.showIcon()) {
        styleClassList.push('p-messages-icon-visible');
      }

      return styleClassList.join(' ');
    });
  }
}
